"use client";

import { useState, useMemo, useEffect, useRef } from "react";
import { useForm, useWatch, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { trackPageEvent } from "@/lib/tracking";
import { trackMetaEvent } from "@/lib/meta-pixel";
import { z } from "zod";
import type {
  PaymentPageData,
  PageCheckoutResult,
} from "@/lib/api/payment-page.schema";
import type { BillingInfo, PaymentMethod } from "@/lib/api/checkout.schema";
import {
  paymentMethodSchema,
  optionalBillingSchema,
} from "@/lib/api/checkout.schema";
import type { Dictionary } from "@/app/[lang]/dictionaries";
import type { Cart } from "@/hooks/use-cart";
import {
  usePageCheckout,
  usePageRetry,
  usePageCancel,
  usePagePolling,
} from "@/hooks/use-payment-page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PhoneInput } from "@/components/ui/phone-input";
import { parsePhoneNumber } from "react-phone-number-input";
import type { Country } from "react-phone-number-input";
import {
  INVALID_PHONE_ERROR,
  MOBILE_MONEY_PHONE_ERROR,
  phoneMarketIssue,
} from "@/lib/phone";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Text } from "@/components/ui/text";
import { Spinner } from "@/components/ui/spinner";
import { PaymentMethodSelector } from "@/app/[lang]/checkout/[token]/_components/payment-method-selector";
import { SupportedProviders } from "@/app/[lang]/checkout/[token]/_components/supported-providers";
import { formatFieldLabel } from "@/app/[lang]/checkout/[token]/_components/payment.utils";
import {
  WaitingMobileView,
  WaitingQRView,
  WaitingCardView,
  PaymentFailedView,
  PaymentExpiredView,
} from "@/app/[lang]/checkout/[token]/_components/views";
import { StatusOverlay } from "@/app/[lang]/checkout/[token]/_components/views/status-overlay";
import { PageSuccessView } from "./page-success-view";
import { CountdownTimer } from "./countdown-timer";
import { createPageAmountFormatter } from "./page-amounts";
import {
  foreignCharge,
  marketCountries,
  type MarketPreview,
} from "@/lib/markets";
import { phoneCountry } from "@/lib/phone";
import { isValidPhoneNumber } from "react-phone-number-input";
import { usePageQuote } from "@/hooks/use-market-quote";
import { formatAmount } from "@/app/[lang]/checkout/[token]/_components/payment.utils";

// Delay before following a post-payment redirect, so the Meta Pixel Purchase
// beacon has time to leave the page before navigation cancels it.
const PIXEL_FLUSH_DELAY_MS = 500;

// Payment state machine — matches the existing PaymentForm pattern
type PaymentState =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "waiting_mobile"; result: PageCheckoutResult }
  | { status: "waiting_qr"; result: PageCheckoutResult }
  | { status: "waiting_card"; result: PageCheckoutResult }
  /** Back from a card page: the result is read from the session alone. */
  | { status: "confirming" }
  | { status: "completed"; result: PageCheckoutResult; redirectUrl?: string | null }
  | { status: "failed"; reason?: string }
  | { status: "expired" };

function buildCheckoutFormSchema(options: {
  collectEmail?: boolean | null;
  hasCardMethod?: boolean;
  customFields?: PaymentPageData["custom_fields"];
  /** Markets whose numbers may pay with mobile money */
  countries: readonly Country[];
}) {
  const base = z
    .object({
      payment_method: paymentMethodSchema,
      customer_name: z.string().min(2, "Full name must be at least 2 characters"),
      customer_phone: z.string().min(1, "Phone number is required"),
      customer_email: options.collectEmail
        ? z.string().min(1, "Email is required").email("Invalid email address")
        : z.string().email("Invalid email address").optional().or(z.literal("")),
      billing: optionalBillingSchema,
    })
    .superRefine((data, ctx) => {
      if (data.payment_method !== "card" && data.customer_phone) {
        const issue = phoneMarketIssue(data.customer_phone, options.countries);
        if (issue) {
          ctx.addIssue({
            path: ["customer_phone"],
            message:
              issue === "unsupported"
                ? MOBILE_MONEY_PHONE_ERROR
                : INVALID_PHONE_ERROR,
            code: z.ZodIssueCode.custom,
          });
        }
      }
      if (
        data.payment_method === "card" &&
        (!data.customer_email?.trim() ||
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.customer_email))
      ) {
        ctx.addIssue({
          path: ["customer_email"],
          message: "Email is required for card payments",
          code: z.ZodIssueCode.custom,
        });
      }
      if (data.payment_method === "card") {
        const b = data.billing;
        const requiredFields = [
          { key: "first_name", label: "First name" },
          { key: "last_name", label: "Last name" },
          { key: "address", label: "Address" },
          { key: "city", label: "City" },
          { key: "state", label: "State" },
          { key: "postcode", label: "Postal code" },
          { key: "country", label: "Country" },
        ] as const;
        for (const { key, label } of requiredFields) {
          if (!b?.[key as keyof typeof b]?.toString().trim()) {
            ctx.addIssue({
              path: ["billing", key],
              message: `${label} is required`,
              code: z.ZodIssueCode.custom,
            });
          }
        }
      }
    });

  if (!options.customFields?.length) {
    return base;
  }

  const fieldSchemas: Record<string, z.ZodTypeAny> = {};
  for (const field of options.customFields) {
    const label = formatFieldLabel(field.label);
    fieldSchemas[field.key] = field.required
      ? z.string().min(1, `${label} is required`)
      : z.string().optional();
  }

  return base.extend({
    custom_data: z.object(fieldSchemas),
  });
}

type CheckoutFormData = {
  payment_method: z.infer<typeof paymentMethodSchema>;
  customer_name: string;
  customer_phone: string;
  customer_email: string | undefined;
  billing?: z.infer<typeof optionalBillingSchema>;
  custom_data?: Record<string, string>;
};

/**
 * Card payments must carry email + full billing on both checkout and retry.
 * Returns undefined for non-card methods so nothing extra is sent.
 */
function cardFieldsFrom(data: CheckoutFormData): {
  customer_email?: string;
  billing?: BillingInfo;
} {
  if (data.payment_method !== "card" || !data.billing) return {};
  const b = data.billing;
  return {
    customer_email: data.customer_email || undefined,
    billing: {
      first_name: b.first_name || "",
      last_name: b.last_name || "",
      address: b.address || "",
      city: b.city || "",
      state: b.state || "",
      postcode: b.postcode || "",
      country: b.country || "",
    },
  };
}

interface PagePaymentFormProps {
  slug: string;
  pageData: PaymentPageData;
  cart: Cart;
  allowedMethods: PaymentMethod[];
  dictionary: Dictionary;
  effectiveAmount: number;
  /** Indicative conversion for the market being previewed */
  preview?: MarketPreview | null;
  /** Reports the payer's phone country so the page can preview that market */
  onPayerCountryChange?: (country: string | null) => void;
  allowCustomAmount?: boolean;
  customAmount?: number;
  minAmount?: number;
  maxAmount?: number;
  meta?: string;
  affiliateCode?: string;
  /** The session a payer coming back from a card page is waiting on. */
  returnReference?: string;
}

export function PagePaymentForm({
  slug,
  pageData,
  cart,
  allowedMethods,
  dictionary,
  effectiveAmount,
  preview = null,
  onPayerCountryChange,
  allowCustomAmount = false,
  customAmount = 0,
  minAmount = 0,
  maxAmount = 0,
  meta,
  affiliateCode,
  returnReference,
}: PagePaymentFormProps) {
  const { checkout, placeholders, paymentPage } = dictionary;

  const [paymentState, setPaymentState] = useState<PaymentState>(
    returnReference ? { status: "confirming" } : { status: "idle" },
  );
  const [sessionReference, setSessionReference] = useState<string | null>(
    returnReference ?? null,
  );

  // Drop the returned session from the address, so a reload or a shared link
  // shows the form, not that payment again
  const forgetReturnReference = () => {
    if (!returnReference) return;
    const url = new URL(window.location.href);
    url.searchParams.delete("ref");
    window.history.replaceState(null, "", url);
  };

  // References whose Purchase pixel event has already fired. Guards against
  // overlapping status polls (3s interval vs. a slower status request) firing
  // Purchase more than once for the same completed session.
  const firedPurchaseRefs = useRef<Set<string>>(new Set());

  const pageCheckout = usePageCheckout(slug);
  const pageRetry = usePageRetry(slug);
  const pageCancel = usePageCancel(slug);

  const countries = useMemo(
    () => marketCountries(pageData.markets),
    [pageData.markets],
  );

  const checkoutFormSchema = useMemo(
    () =>
      buildCheckoutFormSchema({
        collectEmail: pageData.profile?.collect_email,
        hasCardMethod: allowedMethods.includes("card"),
        customFields: pageData.custom_fields,
        countries,
      }),
    [
      pageData.profile?.collect_email,
      pageData.custom_fields,
      allowedMethods,
      countries,
    ],
  );

  const customFieldDefaults = useMemo(() => {
    const defaults: Record<string, string> = {};
    pageData.custom_fields?.forEach((field) => {
      defaults[field.key] = "";
    });
    return defaults;
  }, [pageData.custom_fields]);

  const form = useForm<CheckoutFormData>({
    resolver: zodResolver(checkoutFormSchema),
    defaultValues: {
      payment_method: allowedMethods[0],
      customer_name: "",
      customer_phone: "",
      customer_email: "",
      billing: {
        first_name: "",
        last_name: "",
        address: "",
        city: "",
        state: "",
        postcode: "",
        country: "",
      },
      custom_data: customFieldDefaults,
    },
  });

  // Watch payment method for conditional billing fields
  const selectedPaymentMethod = useWatch({ control: form.control, name: "payment_method" });
  const showBilling = selectedPaymentMethod === "card";

  const customerPhone = useWatch({ control: form.control, name: "customer_phone" });

  // Let the page preview the payer's market as soon as their number is complete
  const payerCountry =
    customerPhone && isValidPhoneNumber(customerPhone)
      ? phoneCountry(customerPhone)
      : null;
  useEffect(() => {
    onPayerCountryChange?.(payerCountry);
  }, [payerCountry, onPayerCountryChange]);

  // The real quote: what this phone will be charged. Indicative until the
  // checkout response confirms it, but a refusal blocks paying.
  const phoneQuote = usePageQuote(
    slug,
    { phone: customerPhone, amount: effectiveAmount },
    selectedPaymentMethod !== "card",
  );
  const quote = phoneQuote.data;
  const quoteRefused = !!quote && !quote.payable;
  const charge = foreignCharge(quote);

  const formatQuotedAmount = createPageAmountFormatter(pageData, cart, preview);
  const buttonText = charge
    ? `${checkout.pay} ${formatAmount(charge.value, charge.currency)}`
    : effectiveAmount > 0
      ? `${checkout.pay} ${formatQuotedAmount(effectiveAmount)}`
      : checkout.pay;

  // Derive billing country from phone number's country code
  useEffect(() => {
    if (!showBilling || !customerPhone) return;
    try {
      const parsed = parsePhoneNumber(customerPhone);
      if (parsed?.country) {
        form.setValue("billing.country", parsed.country);
      }
    } catch {
      // ignore invalid phone while typing
    }
  }, [customerPhone, showBilling, form]);

  const isWaiting =
    paymentState.status === "waiting_mobile" ||
    paymentState.status === "waiting_qr" ||
    paymentState.status === "waiting_card" ||
    paymentState.status === "confirming";

  // Poll for payment status
  usePagePolling({
    slug,
    reference: sessionReference || "",
    enabled: isWaiting && !!sessionReference,
    callbacks: {
      onCompleted: (redirectUrl) => {
        const result =
          paymentState.status === "waiting_mobile" ||
          paymentState.status === "waiting_qr" ||
          paymentState.status === "waiting_card"
            ? paymentState.result
            : null;
        const firedPurchase =
          result && !firedPurchaseRefs.current.has(result.reference);
        if (result && firedPurchase) {
          firedPurchaseRefs.current.add(result.reference);
          trackMetaEvent(
            "Purchase",
            {
              content_name: pageData.title,
              value: result.amount,
              currency: result.currency || "TZS",
            },
            result.reference,
          );
        }
        if (redirectUrl) {
          // Back from the receipt lands on the page, not on this payment again
          forgetReturnReference();
          // Give the Meta Pixel beacon a moment to flush before navigating —
          // an immediate location change cancels the in-flight request and
          // the Purchase event is lost.
          const go = () => {
            window.location.assign(redirectUrl);
          };
          if (firedPurchase) {
            window.setTimeout(go, PIXEL_FLUSH_DELAY_MS);
          } else {
            go();
          }
          return;
        }
        if (result) {
          setPaymentState({ status: "completed", result, redirectUrl });
        }
      },
      onFailed: () => {
        setPaymentState({ status: "failed" });
      },
      onExpired: () => {
        setPaymentState({ status: "expired" });
      },
      onMissing: () => handleRetry(),
    },
  });

  const handleSubmitResult = (
    result: PageCheckoutResult,
    method: PaymentMethod,
  ) => {
    setSessionReference(result.reference);

    // Track payment initiated
    trackPageEvent(slug, { event: "payment_initiated", session_ref: result.reference });

    // If the attempt already failed, show the failed view
    if (result.status === "failed") {
      trackPageEvent(slug, { event: "payment_failed", session_ref: result.reference });
      setPaymentState({ status: "failed" });
      return;
    }

    switch (method) {
      case "card":
        if (result.payment_url) {
          window.location.assign(result.payment_url);
          return;
        }
        setPaymentState({ status: "waiting_card", result });
        break;
      case "qr":
        setPaymentState({ status: "waiting_qr", result });
        break;
      case "mobile_money":
      default:
        setPaymentState({ status: "waiting_mobile", result });
        break;
    }
  };

  const onSubmit = form.handleSubmit(async (data) => {
    setPaymentState({ status: "submitting" });

    try {
      let result: PageCheckoutResult;

      if (sessionReference) {
        // Retry on existing session
        result = await pageRetry.mutateAsync({
          session_reference: sessionReference,
          payment_method: data.payment_method,
          customer_name: data.customer_name,
          customer_phone: data.customer_phone,
          ...cardFieldsFrom(data),
        });
      } else {
        // Fresh checkout
        const params: {
          customer_name: string;
          customer_phone: string;
          customer_email?: string;
          payment_method: PaymentMethod;
          amount?: number;
          billing?: BillingInfo;
          items?: { stock_id: string; quantity: number }[];
          custom_data?: Record<string, unknown>;
          meta?: string;
          affiliate_code?: string;
        } = {
          customer_name: data.customer_name,
          customer_phone: data.customer_phone,
          payment_method: data.payment_method,
        };

        if (data.customer_email) {
          params.customer_email = data.customer_email;
        }

        // Card payments also need billing
        const { billing } = cardFieldsFrom(data);
        if (billing) {
          params.billing = billing;
        }

        if (pageData.page_type === "catalog" && cart.cartItems.length > 0) {
          params.items = cart.cartItems.map((entry) => ({
            stock_id: entry.item.id,
            quantity: entry.quantity,
          }));
        }

        // Include amount when allow_custom_amount is true (required by API)
        if (allowCustomAmount && customAmount >= minAmount && customAmount <= maxAmount) {
          params.amount = customAmount;
        }

        if (meta) {
          params.meta = meta;
        }

        if (affiliateCode) {
          params.affiliate_code = affiliateCode;
        }

        // Include custom field values
        if (data.custom_data && Object.keys(data.custom_data).length > 0) {
          const filledData: Record<string, unknown> = {};
          for (const [key, value] of Object.entries(data.custom_data)) {
            if (value !== "" && value !== undefined) {
              filledData[key] = value;
            }
          }
          if (Object.keys(filledData).length > 0) {
            params.custom_data = filledData;
          }
        }

        result = await pageCheckout.mutateAsync(params);
      }

      trackPageEvent(slug, { event: "click_pay", session_ref: result.reference });
      trackMetaEvent("InitiateCheckout", {
        content_name: pageData.title,
        value: result.amount,
        currency: result.currency || "TZS",
      });
      handleSubmitResult(result, data.payment_method);
    } catch {
      setPaymentState({ status: "idle" });
    }
  });

  const handleCancel = async () => {
    try {
      const result =
        paymentState.status === "waiting_mobile" ||
        paymentState.status === "waiting_qr" ||
        paymentState.status === "waiting_card"
          ? paymentState.result
          : null;
      if (result?.attempt_id) {
        await pageCancel.mutateAsync(result.attempt_id);
      }
    } catch {
      // Continue even if cancel fails
    }
    setSessionReference(null);
    setPaymentState({ status: "idle" });
  };

  const handleRetry = () => {
    setSessionReference(null);
    setPaymentState({ status: "idle" });
    forgetReturnReference();
  };

  // ---- Render based on payment state ----

  if (paymentState.status === "waiting_mobile") {
    return (
      <div className="space-y-4">
        <WaitingMobileView
          dictionary={dictionary}
          onCancel={handleCancel}
          amount={paymentState.result.charged_amount ?? paymentState.result.amount}
          currency={paymentState.result.charged_currency ?? paymentState.result.currency}
        />
        <div className="text-center">
          <CountdownTimer
            expiresAt={paymentState.result.expires_at}
            label={paymentPage.expiresIn}
            onExpired={() => setPaymentState({ status: "expired" })}
          />
        </div>
      </div>
    );
  }

  if (paymentState.status === "waiting_qr") {
    return (
      <div className="space-y-4">
        <WaitingQRView
          qrCode={paymentState.result.qr_code!}
          paymentToken={paymentState.result.payment_token ?? undefined}
          dictionary={dictionary}
          onCancel={handleCancel}
        />
        <div className="text-center">
          <CountdownTimer
            expiresAt={paymentState.result.expires_at}
            label={paymentPage.expiresIn}
            onExpired={() => setPaymentState({ status: "expired" })}
          />
        </div>
      </div>
    );
  }

  if (paymentState.status === "confirming") {
    return (
      <StatusOverlay
        icon={<Spinner className="size-10" />}
        title={dictionary.waiting.confirming.title}
        description={dictionary.waiting.confirming.description}
        cancelLabel={dictionary.waiting.confirming.back}
        onCancel={handleRetry}
      />
    );
  }

  if (paymentState.status === "waiting_card") {
    return (
      <WaitingCardView
        paymentUrl={paymentState.result.payment_url ?? ""}
        dictionary={dictionary}
        onCancel={handleCancel}
      />
    );
  }

  if (paymentState.status === "completed") {
    return (
      <PageSuccessView
        result={paymentState.result}
        merchantName={pageData.profile?.merchant_name || "Merchant"}
        dictionary={dictionary}
        onContinue={() => {
          setSessionReference(null);
          setPaymentState({ status: "idle" });
          cart.clearCart();
        }}
      />
    );
  }

  if (paymentState.status === "failed") {
    return (
      <PaymentFailedView
        reason={paymentState.reason}
        onRetry={handleRetry}
        dictionary={dictionary}
      />
    );
  }

  if (paymentState.status === "expired") {
    return <PaymentExpiredView onRetry={handleRetry} dictionary={dictionary} />;
  }

  // ---- Main form (idle / submitting) ----

  const isSubmitting =
    paymentState.status === "submitting" ||
    pageCheckout.isPending ||
    pageRetry.isPending;

  const isCustomAmountInvalid =
    allowCustomAmount &&
    (customAmount < minAmount || customAmount > maxAmount || customAmount <= 0);

  const isDisabled =
    isSubmitting ||
    (pageData.page_type === "catalog" && cart.totalItems === 0) ||
    isCustomAmountInvalid ||
    quoteRefused;

  return (
    <div className="flex h-full flex-col">
      <form id="payment-form" onSubmit={onSubmit} className="space-y-5">
        {/* Payment method selection */}
        <PaymentMethodSelector
          control={form.control}
          allowedMethods={allowedMethods}
          dictionary={dictionary}
        />

        {/* Phone number */}
        <div>
          <Text variant="small" className="font-medium mb-2.5">
            {checkout.phoneNumber}
          </Text>
          <Controller
            name="customer_phone"
            control={form.control}
            render={({ field, fieldState }) => (
              <div>
                <PhoneInput
                  key={selectedPaymentMethod}
                  id={field.name}
                  value={field.value || undefined}
                  onChange={(val) => field.onChange(val ?? "")}
                  placeholder={placeholders.phone}
                  aria-invalid={fieldState.invalid}
                  countries={
                    selectedPaymentMethod === "card"
                      ? undefined
                      : [...countries]
                  }
                />
                {fieldState.error ? (
                  <Text
                    variant="muted"
                    className="mt-1 text-xs text-destructive"
                  >
                    {fieldState.error.message}
                  </Text>
                ) : quoteRefused ? (
                  <Text
                    variant="muted"
                    className="mt-1 text-xs text-destructive"
                  >
                    {quote.message || MOBILE_MONEY_PHONE_ERROR}
                  </Text>
                ) : charge ? (
                  <Text variant="muted" className="mt-1 text-xs">
                    {checkout.phoneCharged}{" "}
                    {formatAmount(charge.value, charge.currency)}
                  </Text>
                ) : null}
              </div>
            )}
          />
        </div>

        {/* Customer name & email */}
        <div>
          <Text variant="small" className="font-medium mb-2.5">
            {checkout.contactInfo}
          </Text>
          <div className="overflow-hidden rounded-lg border border-border">
            <Controller
              name="customer_name"
              control={form.control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  id={field.name}
                  placeholder={checkout.fullName}
                  aria-invalid={fieldState.invalid}
                  className={`rounded-none border-0 h-11 ${(pageData.profile?.collect_email || selectedPaymentMethod === "card") ? "border-b" : ""}`}
                />
              )}
            />
            {(pageData.profile?.collect_email || selectedPaymentMethod === "card") && (
              <Controller
                name="customer_email"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    id={field.name}
                    type="email"
                    placeholder={checkout.emailAddress}
                    aria-invalid={fieldState.invalid}
                    className="rounded-none border-0 h-11"
                  />
                )}
              />
            )}
          </div>
          {(form.formState.errors.customer_name ||
            form.formState.errors.customer_email) && (
            <Text variant="muted" className="mt-1 text-xs text-destructive">
              {form.formState.errors.customer_name?.message ||
                form.formState.errors.customer_email?.message}
            </Text>
          )}
        </div>

        {/* Billing fields for card payments — country derived from phone */}
        {showBilling && (
          <div>
            <Text variant="small" className="font-medium mb-2.5">
              {checkout.billingInfo}
            </Text>
            <div className="overflow-hidden rounded-lg border border-border">
              <div className="grid grid-cols-2">
                <Controller
                  name="billing.first_name"
                  control={form.control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      value={field.value || ""}
                      placeholder={checkout.firstName}
                      aria-invalid={fieldState.invalid}
                      autoComplete="billing given-name"
                      className="rounded-none border-0 border-b border-r h-11"
                    />
                  )}
                />
                <Controller
                  name="billing.last_name"
                  control={form.control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      value={field.value || ""}
                      placeholder={checkout.lastName}
                      aria-invalid={fieldState.invalid}
                      autoComplete="billing family-name"
                      className="rounded-none border-0 border-b h-11"
                    />
                  )}
                />
              </div>
              <Controller
                name="billing.address"
                control={form.control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    value={field.value || ""}
                    placeholder={checkout.address}
                    aria-invalid={fieldState.invalid}
                    autoComplete="billing street-address"
                    className="rounded-none border-0 border-b h-11"
                  />
                )}
              />
              <Controller
                name="billing.city"
                control={form.control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    value={field.value || ""}
                    placeholder={checkout.city}
                    aria-invalid={fieldState.invalid}
                    autoComplete="billing address-level2"
                    className="rounded-none border-0 border-b h-11"
                  />
                )}
              />
              <div className="grid grid-cols-2">
                <Controller
                  name="billing.state"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      value={field.value || ""}
                      placeholder={checkout.state}
                      aria-invalid={fieldState.invalid}
                      autoComplete="billing address-level1"
                      className="rounded-none border-0 border-r h-11"
                    />
                  )}
                />
                <Controller
                  name="billing.postcode"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      value={field.value || ""}
                      placeholder={checkout.postcode}
                      aria-invalid={fieldState.invalid}
                      autoComplete="billing postal-code"
                      className="rounded-none border-0 h-11"
                    />
                  )}
                />
              </div>
            </div>
          </div>
        )}

        {/* Custom fields */}
        {pageData.custom_fields?.map((customField) => (
          <div key={customField.key}>
            <Label htmlFor={`custom_data.${customField.key}`} className="mb-2.5">
              {formatFieldLabel(customField.label)}
              {!customField.required && (
                <span className="text-muted-foreground/70 font-normal">
                  {" "}
                  ({checkout.optional})
                </span>
              )}
            </Label>
            <Controller
              name={`custom_data.${customField.key}`}
              control={form.control}
              render={({ field, fieldState }) => (
                <div>
                  {customField.type === "textarea" ? (
                    <Textarea
                      id={field.name}
                      name={field.name}
                      value={(field.value as string) || ""}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder={customField.placeholder}
                      aria-invalid={fieldState.invalid}
                      rows={3}
                    />
                  ) : customField.type === "select" ? (
                    <Select
                      value={(field.value as string) || ""}
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger
                        id={field.name}
                        className="h-11 w-full"
                        aria-invalid={fieldState.invalid}
                        onBlur={field.onBlur}
                      >
                        <SelectValue
                          placeholder={
                            customField.placeholder ||
                            formatFieldLabel(customField.label)
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {customField.options?.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : customField.type === "phone" ? (
                    <PhoneInput
                      key={selectedPaymentMethod}
                      id={field.name}
                      value={(field.value as string) || ""}
                      onChange={(val) => field.onChange(val || "")}
                      placeholder={customField.placeholder}
                      aria-invalid={fieldState.invalid}
                      countries={
                        selectedPaymentMethod === "card"
                          ? undefined
                          : [...countries]
                      }
                    />
                  ) : customField.type === "date" ? (
                    <DatePicker
                      id={field.name}
                      value={(field.value as string) || ""}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder={
                        customField.placeholder ||
                        formatFieldLabel(customField.label)
                      }
                      aria-invalid={fieldState.invalid}
                    />
                  ) : (
                    <Input
                      {...field}
                      value={(field.value as string) || ""}
                      id={field.name}
                      type={
                        customField.type === "email"
                          ? "email"
                          : customField.type === "number"
                            ? "number"
                            : "text"
                      }
                      placeholder={customField.placeholder}
                      aria-invalid={fieldState.invalid}
                      className="h-11"
                    />
                  )}
                  {fieldState.error && (
                    <Text
                      variant="muted"
                      className="mt-1 text-xs text-destructive"
                    >
                      {fieldState.error.message}
                    </Text>
                  )}
                </div>
              )}
            />
          </div>
        ))}

        {/* Submit button */}
        <div className="pt-2">
          <Button
            type="submit"
            size="lg"
            className="w-full h-12"
            disabled={isDisabled}
          >
            {isSubmitting ? <Spinner className="size-4" /> : buttonText}
          </Button>

          {(pageCheckout.isError || pageRetry.isError) && (
            <Text
              variant="muted"
              className="mt-2 text-center text-xs text-destructive"
            >
              {pageCheckout.error?.message || pageRetry.error?.message}
            </Text>
          )}
        </div>
      </form>

      {/* Payment provider logos */}
      <div className="mt-6">
        <SupportedProviders dictionary={dictionary} />
      </div>
    </div>
  );
}
